import { useContext } from "react";
import { UserContext } from "../App";
import { SessionKey } from "../common/constants";
import { triggerAnalyticsEvent } from "../analytics/createEventTrigger";
import { ROUTE_SKILLBUILDER_ADD_SEATS, ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, ROUTE_SKILLBUILDER_SUBSCRIPTION_REQUEST, ROUTE_SKILLBUILDER_UPDATE_SUBSCRIPTION_RENEWAL } from "../common/routes";
import { useNavigate } from "react-router-dom";
import { useAWSAccountValidation } from "../user/api/queries";
import { getNodeEnvironment } from "../common/nodeEnvironment";
import { useTranslation } from "react-i18next";
import { matchRegex } from "../common/utilityFunctions";
export const useAccountValidation = (awsAccountId) => {
    const useValidationSuccess = () => {
        triggerAnalyticsEvent("click", `SBTS_${getNodeEnvironment()} | aws_sign | authentication | event_succeeded`);
        // Check if we're in an "add seats" flow
        const parentSubscriptionRequestId = sessionStorage.getItem(SessionKey.PARENT_SUBSCRIPTION_REQUEST_ID);
        const requestType = sessionStorage.getItem(SessionKey.REQUEST_TYPE);
        const subscriptionRequestId = sessionStorage.getItem(SessionKey.SUBSCRIPTION_REQUEST_ID);
        if (parentSubscriptionRequestId && requestType === "midTermAddonRequest") {
            // Navigate to add seats page
            navigate(ROUTE_SKILLBUILDER_ADD_SEATS, {
                replace: true
            });
        }
        else if (subscriptionRequestId && requestType === "subscriptionRenewalRequest") {
            // Navigate to update subscription renewal page
            navigate(ROUTE_SKILLBUILDER_UPDATE_SUBSCRIPTION_RENEWAL, {
                replace: true
            });
        }
        else {
            // Subscription configuration flow
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_REQUEST, {
                replace: true
            });
        }
    };
    const useValidationError = (error) => {
        const INVALID_ATP_ACCOUNT_ERROR_REGEX = /^ATP user accountId \d+ not found$/i;
        const MAX_SUBSCRIPTION_REACHED = 'Customer has maxed out ACTIVE Skill-builder subscriptions'.toLowerCase();
        const C_SCORE_LOW = 'AWS Account Not Authroized'.toLowerCase();
        const INVALID_EMAIL_DOMAIN = 'Invalid email domain'.toLowerCase();
        const AWS_ACCOUNT_NOT_ACTIVE = 'has invalid state'.toLowerCase();
        const errorMessageFromServer = error?.message;
        sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_ID);
        sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_JWT);
        sessionStorage.removeItem(SessionKey.IS_AMAZON_TRAINING_PARTNER);
        sessionStorage.removeItem(SessionKey.PARENT_SUBSCRIPTION_REQUEST_ID);
        sessionStorage.removeItem(SessionKey.SUBSCRIPTION_REQUEST_ID);
        if (errorMessageFromServer && matchRegex(error.message, INVALID_ATP_ACCOUNT_ERROR_REGEX)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed_of_non_atp_aws_account`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_request_aws_account_id_not_atp_error_text', { awsAccountId }), type: 'error' } } });
        }
        else if (errorMessageFromServer.toLowerCase().includes(MAX_SUBSCRIPTION_REACHED)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed_of_max_subscriptions`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_form_max_subscription_limit_reached'), type: 'error' } } });
        }
        else if (errorMessageFromServer.toLowerCase().includes(C_SCORE_LOW)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed_of_low_c_score`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_form_c_score_low', { awsAccountId: awsAccountId }), type: 'error' } } });
        }
        else if (errorMessageFromServer.toLowerCase().includes(INVALID_EMAIL_DOMAIN)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed_of_inavlida_email_domain`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_form_invalid_email_domain', { awsAccountId: awsAccountId }), type: 'error' } } });
        }
        else if (errorMessageFromServer.toLowerCase().includes(AWS_ACCOUNT_NOT_ACTIVE)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed_of_inactive_aws_account`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_form_account_not_active', { awsAccountId: awsAccountId }), type: 'error' } } });
        }
        else {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | aws_sign | account_validation | event_failed`);
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: t('subscription_form_something_went_wrong'), type: 'error' } } });
        }
    };
    const user = useContext(UserContext);
    const navigate = useNavigate();
    const { t } = useTranslation();
    return useAWSAccountValidation(user?.email, awsAccountId, user?.id, useValidationSuccess, useValidationError);
};
